import * as Headless from '@headlessui/react';
import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { Textarea } from '@oysterjs/ui/textarea';

import {
  CaraWorkspaceArtifactCommentResolvedBy,
  CaraWorkspaceArtifactCommentTier
} from '~/api/graphql';

import { type CommentDraft, isRegionDraft } from '~/components/workspaces/lib/comment-draft';
import {
  type ArtifactComment,
  isOpenComment
} from '~/components/workspaces/hooks/use-artifact-comments';

const QUOTE_CHARS = 140;

const clip = (text: string) =>
  text.length > QUOTE_CHARS ? `${text.slice(0, QUOTE_CHARS - 1)}…` : text;

/** The text a comment points at as it reads now, and a word for how sure that is. */
const placementOf = (comment: ArtifactComment): { text: string; note: string | null } => {
  if (comment.region && !comment.anchor.quote) {
    return { text: `Page ${comment.region.page}, marked area`, note: null };
  }
  const tier = comment.placement?.tier;
  if (tier === CaraWorkspaceArtifactCommentTier.Orphan) {
    return { text: comment.anchor.quote, note: 'Text gone' };
  }
  if (tier === CaraWorkspaceArtifactCommentTier.Nearest) {
    return { text: comment.placement?.matchedText || comment.anchor.quote, note: 'Moved' };
  }
  return { text: comment.anchor.quote, note: null };
};

const closedNote = (comment: ArtifactComment) =>
  comment.resolvedBy === CaraWorkspaceArtifactCommentResolvedBy.Cara ? 'Sent to Cara' : 'Done';

/** Where an area was drawn, when that is not the layout shown. */
const earlierLayoutOf = (
  comment: ArtifactComment,
  currentVersion?: number,
  currentRevision?: number
) => {
  const region = comment.region;
  if (!region) return null;
  const earlierVersion = currentVersion != null && region.version !== currentVersion;
  const earlierRevision = currentRevision != null && region.revision !== currentRevision;
  if (!earlierVersion && !earlierRevision) return null;
  return `Recorded on version ${region.version}, revision ${region.revision}`;
};

export const DocumentCommentsPanel = (props: {
  comments: ArtifactComment[];
  currentVersion?: number;
  currentRevision?: number;
  draft: CommentDraft | null;
  body: string;
  onBodyChange: (body: string) => void;
  activeId: string | null;
  busy: boolean;
  loading?: boolean;
  error: string | null;
  canEdit: boolean;
  /** Send to Cara revises the latest version's text, so it waits on an earlier version. */
  latest?: boolean;
  onAdd: (body: string) => void;
  onCancelDraft: () => void;
  onSelect: (comment: ArtifactComment) => void;
  onResolve: (id: string) => void;
  onDelete: (id: string) => void;
  onSend: () => void;
  onClose: () => void;
  onRetry?: () => void;
}) => {
  const { body } = props;
  const latest = props.latest ?? true;
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const open = props.comments.filter(isOpenComment);
  const closed = props.comments.filter((c) => !isOpenComment(c));
  const submit = () => {
    if (props.canEdit && body.trim()) props.onAdd(body.trim());
  };

  return (
    <aside
      aria-label="Comments"
      className="mt-2 flex w-72 shrink-0 flex-col rounded-lg border border-neutral-200 bg-white min-h-0 dark:border-neutral-700 dark:bg-neutral-800"
    >
      <div className="flex items-center justify-between gap-2 border-b border-neutral-200 px-3 py-2 dark:border-neutral-700">
        <div className="text-sm font-medium text-neutral-900 dark:text-white">Comments</div>
        <Headless.Button
          aria-label="Close comments"
          onClick={props.onClose}
          className="rounded p-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-white/10 dark:hover:text-white"
        >
          <XMarkIcon className="size-4" />
        </Headless.Button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-3">
        {props.draft && props.canEdit && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-2 space-y-2 dark:border-amber-500/30 dark:bg-amber-500/10">
            <blockquote className="text-xs text-neutral-700 border-l-2 border-amber-400 pl-2 dark:text-neutral-300">
              {isRegionDraft(props.draft)
                ? `Page ${props.draft.region.page}, marked area`
                : clip(props.draft.candidate.quote)}
            </blockquote>
            <Textarea
              aria-label="Comment"
              autoFocus
              rows={3}
              value={body}
              placeholder="What should change here?"
              onChange={(e) => props.onBodyChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
                if (e.key === 'Escape' && !props.busy) props.onCancelDraft();
              }}
            />
            {props.error && (
              <p
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
              >
                {props.error}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button color="white" onClick={props.onCancelDraft} disabled={props.busy}>
                Cancel
              </Button>
              <Button color="sky" onClick={submit} disabled={props.busy || !body.trim()}>
                {props.error ? 'Try again' : 'Add'}
              </Button>
            </div>
          </div>
        )}

        {props.error && !props.draft && (
          <p
            role="alert"
            className="rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
          >
            {props.error}
            {props.onRetry && (
              <button
                type="button"
                className="ml-2 underline"
                onClick={props.onRetry}
                disabled={props.busy}
              >
                Reload comments
              </button>
            )}
          </p>
        )}

        {props.comments.length === 0 && !props.draft && !props.error && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {props.loading
              ? 'Loading comments…'
              : props.canEdit && latest
                ? 'Select text or drag an area on a page to comment.'
                : 'No comments yet.'}
          </p>
        )}

        {[...open, ...closed].map((comment) => {
          const placement = placementOf(comment);
          const earlierLayout = earlierLayoutOf(
            comment,
            props.currentVersion,
            props.currentRevision
          );
          const isOpen = isOpenComment(comment);
          return (
            <div
              key={comment.id}
              data-testid="document-comment"
              data-open={isOpen ? 'true' : 'false'}
              className={`rounded-md border p-2 ${
                comment.id === props.activeId
                  ? 'border-amber-400'
                  : 'border-neutral-200 dark:border-neutral-700'
              } ${isOpen ? '' : 'opacity-60'}`}
            >
              <button
                type="button"
                className="w-full text-left"
                onClick={() => props.onSelect(comment)}
              >
                <span className="block text-xs text-neutral-600 border-l-2 border-neutral-300 pl-2 dark:text-neutral-400 dark:border-neutral-600">
                  {clip(placement.text)}
                </span>
                <span className="block mt-1 text-sm text-neutral-900 whitespace-pre-wrap dark:text-white">
                  {comment.body}
                </span>
              </button>
              <div className="mt-1.5 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                  {earlierLayout && (
                    <Badge color="neutral" title={earlierLayout}>
                      Earlier layout
                    </Badge>
                  )}
                  {placement.note && <Badge color="neutral">{placement.note}</Badge>}
                  {!isOpen && <Badge color="neutral">{closedNote(comment)}</Badge>}
                </div>
                {isOpen && props.canEdit && (
                  <div className="flex items-center gap-2 text-xs">
                    <button
                      type="button"
                      className="text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
                      disabled={props.busy}
                      onClick={() => props.onResolve(comment.id)}
                    >
                      Done
                    </button>
                    <button
                      type="button"
                      className="text-neutral-600 hover:text-red-700 dark:text-neutral-400 dark:hover:text-red-400"
                      disabled={props.busy}
                      onClick={() => {
                        if (deletingId !== comment.id) setDeletingId(comment.id);
                        else {
                          props.onDelete(comment.id);
                          setDeletingId(null);
                        }
                      }}
                    >
                      {deletingId === comment.id ? 'Confirm delete' : 'Delete'}
                    </button>
                    {deletingId === comment.id && (
                      <button
                        type="button"
                        onClick={() => setDeletingId(null)}
                        disabled={props.busy}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {open.length > 0 && (
        <div className="border-t border-neutral-200 px-3 py-2 dark:border-neutral-700">
          <Button
            color="sky"
            className="w-full"
            onClick={props.onSend}
            disabled={props.busy || !props.canEdit || !latest}
            title={latest ? undefined : 'Open the latest version to send comments to Cara.'}
          >
            {`Send ${open.length} to Cara`}
          </Button>
        </div>
      )}
    </aside>
  );
};
